import {
  GET_ALL_FORM_RESPONSES_FOR_COMPANY_BY_DATE_RANGE,
  GET_EMPLOYEES_FOR_CURRENT_COMPANY,
} from '../api';
import { useApolloClient } from '@apollo/client';

export type CompleteSurveyResponseRequestInput = {
  /** Spans every version of the template. */
  templateId: string;
  /** ISO timestamp marking the inclusive start of the range. */
  startDate: string;
  /** ISO timestamp marking the inclusive end of the range. */
  endDate: string;
  /**
   * When true, responses come back without any employee identity attached.
   * Callers should pass true if *any* version of the template is anonymous.
   */
  isAnonymous: boolean;
};

export const useCompleteSurveyResponseRequest = () => {
  const apolloClient = useApolloClient();

  return async ({
    templateId,
    startDate,
    endDate,
    isAnonymous,
  }: CompleteSurveyResponseRequestInput) => {
    const {
      data: { allFormResponsesForCompanyByDateRange },
    } = await apolloClient.query({
      query: GET_ALL_FORM_RESPONSES_FOR_COMPANY_BY_DATE_RANGE,
      variables: {
        input: {
          templateId,
          startDate,
          endDate,
        },
      },
      fetchPolicy: 'no-cache',
    });

    const responses = allFormResponsesForCompanyByDateRange ?? [];

    if (isAnonymous) {
      return responses;
    }

    // `employees` includes archived employees; `activeEmployees` does not, and
    // matching against it used to silently drop every response submitted by
    // someone who has since left the company.
    const { data: employeeData } = await apolloClient.query({
      query: GET_EMPLOYEES_FOR_CURRENT_COMPANY,
      fetchPolicy: 'no-cache',
    });

    const employeesByKey = new Map<string, any>();
    (employeeData?.employees ?? []).forEach((employee: any) => {
      // Responses reference an employee by business id, but a few older rows
      // store the record id instead.
      if (employee.employeeId) employeesByKey.set(employee.employeeId, employee);
      if (employee.id) employeesByKey.set(employee.id, employee);
    });

    return responses.map((response: any) => {
      const employee = employeesByKey.get(response.employeeId);
      return {
        ...response,
        employeeFirstName: employee?.firstName ?? '',
        employeeLastName: employee?.lastName ?? '',
        employeeEmailAddress: employee?.emailAddress ?? '',
      };
    });
  };
};
